import { effect, Injectable, signal } from '@angular/core';
import { Filter, StoredFilter, ItemHighlight, DisplayOptions, FilterSource } from '../models/filter-models';
import { flattenCosmeticCustomRulesInOrder, flattenCustomRulesInOrder, getDefaultCustomRuleGroup } from '../common/custom-rules/custom-rule-groups-helper';
import { VERSION, } from '../const';
import { FilterGeneratorService, FilterResultData } from './filter-generator.service';
import { patchVersion } from '../../version';
import { FilterSectionsToUpdate } from '../models/filter-import.models';
import { CurrencyTierService } from './tiering/currency-tier.service';
import { ChanceBaseTierType, GearTierType, JewelleryTierType, ModTierType, Tier, UniqueTierType, TieredMod } from '../models/item-data-models';
import { ChanceBasesTierService } from './tiering/chance-bases-tier.service';
import { UniquesTierService } from './tiering/uniques-tier.service';
import { GearTierService } from './tiering/gear-tier.service';
import { JewelleryTierService } from './tiering/jewellery-tier.service';
import { EquipmentTierService } from './tiering/equipment-tier.service';
import { ItemDataService } from './item-data.service';
import { DEFAULT_FILTER_NAME, poe1 } from '../../game';
import { ModTierService } from './tiering/mod-tier.service';
import { ClusterJewelTierService } from './tiering/cluster-jewel-tier.service';
import { CosmeticPresetsService } from './cosmetic-presets.service';
import { logError } from '../app.config';
import { FilterStoreService } from './filter-store.service';
import { FilterIdService } from './filter-id.service';

@Injectable({
  providedIn: 'root'
})
export class FilterService {
  filterResultData = signal<FilterResultData>(new FilterResultData());
  dirtyFilterUpdated = signal<number>(0); // Allows a dirty way to check for a updated filter; to update styles

  // Expose store service properties for backward compatibility
  get storedFilters(): StoredFilter[] {
    return this.filterStoreService.storedFilters;
  }

  get activeFilter() {
    return this.filterStoreService.activeStoredFilter;
  }

  get activeFilterName() {
    return this.filterStoreService.activeFilterName;
  }

  constructor(
    private filterStoreService: FilterStoreService,
    private filterGeneratorService: FilterGeneratorService,
    private currencyTierService: CurrencyTierService,
    private uniquesTierService: UniquesTierService,
    private chanceBaseTierService: ChanceBasesTierService,
    private uniqueTierService: UniquesTierService,
    private gearTierService: GearTierService,
    private jewelleryTierService: JewelleryTierService,
    private equipmentTierService: EquipmentTierService,
    private itemDataService: ItemDataService,
    private modTierService: ModTierService,
    private clusterJewelTierService: ClusterJewelTierService,
    private cosmeticPresetsService: CosmeticPresetsService,
    private filterIdService: FilterIdService,
  ) {
    // Initialize with new filter if none exists
    if (this.filterStoreService.storedFilters.length === 0) {
      const newFilter = this.createNewFilter();
      const storedFilter: StoredFilter = {
        name: DEFAULT_FILTER_NAME,
        filter: newFilter,
        lastUpdated: new Date().toISOString(),
        version: VERSION,
        build: patchVersion
      };
      this.filterStoreService.initializeWithNewFilter(storedFilter);
    }

    this.setActiveFilter(this.activeFilterName(), 'active');
    this.updateFilter(true);

    // Initialize equipment and jewellery tier list data (synchronous, no effect needed)
    this.equipmentTierService.combineEquipmentTierListData(this.activeFilter().filter);
    this.jewelleryTierService.combineJewelleryTierListData(this.activeFilter().filter);

    effect(() => {
      this.itemDataService.getDivinationCardsData()();
      this.currencyTierService.combineCurrencyTierListData(this.activeFilter().filter);
    });

    effect(() => {
      this.itemDataService.getGearData()();
      this.gearTierService.combineGearTierListData(this.activeFilter().filter);
    });

    effect(() => {
      this.itemDataService.getModData()();
      this.modTierService.combineModTierListData(this.activeFilter().filter);
    });
  }

  setActiveFilter(name: string, source: FilterSource): void {
    const selectedFilter = this.filterStoreService.setActiveFilter(name, source);

    this.updateAllTiers(selectedFilter.filter);
    this.updateFilter();
  }

  updateFilter(disableCache = false) {
    const filter = this.activeFilter();

    const result = this.filterGeneratorService.updateFilter(filter, disableCache);
    this.filterResultData.set(result);

    // Update the filter metadata in store
    this.filterStoreService.updateFilterMetadata(filter);
    this.dirtyFilterUpdated.update(c => c + 1);
  }

  setFilterId(name: string, id: string) {
    this.filterStoreService.setFilterId(name, id);
  }

  addFilter(filter: StoredFilter) {
    this.filterStoreService.addFilter(filter);
  }

  removeFilter(name: string) {
    this.filterStoreService.removeFilter(name);
  }

  updateAllEconomyTiers() {
    const filter = this.filterStoreService.activeFilter();
    this.currencyTierService.setTieringMode(filter.tieringMode, filter);
    this.uniqueTierService.setTieringMode(filter.uniquesTieringMode, filter);
    if (poe1) this.clusterJewelTierService.setTieringMode(filter.clusterJewelTieringMode, filter);
    this.chanceBaseTierService.initialize(filter);
  }

  updateAllTiers(specificFilter?: Filter) {
    const filter = specificFilter ?? this.filterStoreService.activeFilter();

    this.currencyTierService.setTieringMode(filter.tieringMode, filter);
    this.uniqueTierService.setTieringMode(filter.uniquesTieringMode, filter);
    this.chanceBaseTierService.initialize(filter);
    this.equipmentTierService.setTieringMode(filter.equipmentTieringMode, filter);
    this.modTierService.combineModTierListData(filter);
    this.jewelleryTierService.combineJewelleryTierListData(filter);
    this.gearTierService.combineGearTierListData(filter);
    if (poe1) this.clusterJewelTierService.setTieringMode(filter.clusterJewelTieringMode, filter);
  }

  resetFilter() {
    const stored = this.filterStoreService.activeStoredFilter();
    stored.filter = this.createNewFilter();
    this.filterStoreService.activeStoredFilter.set({ ...stored });
    this.updateAllTiers();
    this.updateFilter();
    this.filterStoreService.dirtyFilterChanged.update(c => c + 1);
  }

  createNewFilter(): Filter {
    const newFilter = new Filter();
    newFilter.id = this.filterIdService.generateId();
    this.cosmeticPresetsService.applyPreset(newFilter);
    return newFilter;
  }

  replaceFilter(filter: Filter) {
    const stored = this.filterStoreService.activeStoredFilter();
    stored.filter = filter;
    this.filterStoreService.activeStoredFilter.set({ ...stored });
    this.updateAllTiers();
    this.updateFilter();
    this.filterStoreService.dirtyFilterChanged.update(c => c + 1);
  }

  updateFilterName(oldName: string, newName: string) {
    this.filterStoreService.updateFilterName(oldName, newName);
  }

  saveFilters() {
    this.filterStoreService.saveFilters();
  }

  updateFilterSections(fromFilter: Filter, sections: FilterSectionsToUpdate, toFilter?: Filter) {
    const filter = toFilter ?? this.filterStoreService.activeFilter();

    const specialProperties: (keyof Filter)[] = ['id', 'customRuleGroups', 'customCosmeticRuleGroups', 'tierCosmetics', 'uniqueTierCosmetics', 'chanceBaseTierCosmetics', 'divCardTierCosmetics', 'equipmentTierCosmetics',
      'freeRulesTop', 'freeRulesBottom', 'customCurrencyTiers', 'customUniqueTiers', 'customChanceBaseTiers', 'customModTiers', 'customGearTiers', 'customJewelleryTiers', 'customEquipmentTiers',
      'customModItemCategories', 'weaponFilters', 'armourFilters', 'jewelleryFilters', 'jewelFilters', 'flaskFilters', 'identifiedItemConfigs',
      'tieringModeCustom', 'uniquesTieringModeCustom'];

    if (sections.quickFilters) {
      Object.keys(fromFilter).forEach(key => {
        if (!specialProperties.includes(key as keyof Filter)) {
          const newFilterItemValue = fromFilter[key as keyof Filter];
          if (newFilterItemValue && key.startsWith('highlight') && typeof newFilterItemValue === 'object' && !Array.isArray(newFilterItemValue)) {
            const newHighlight = newFilterItemValue as ItemHighlight;
            if (!newHighlight.displayOptions) {
              logError('No display options when trying to update highlight, ignoring', key);
            } else {
              const activeHighlight = filter[key as keyof Filter] as ItemHighlight;
              (filter as any)[key].displayOptions = new DisplayOptions({ // TODO: Still doesn't update the view
                ...activeHighlight.displayOptions,
                active: newHighlight.displayOptions.active,
                hidden: newHighlight.displayOptions.hidden
              });
            }
          } else if (Array.isArray(newFilterItemValue)) {
            (filter as any)[key] = structuredClone(newFilterItemValue);
          } else {
            (filter as any)[key] = newFilterItemValue;
          }
        }
      });
    }

    if (sections.myWeapons) {
      if (sections.appendToMyEquipment) {
        filter.weaponFilters = filter.weaponFilters.concat(structuredClone(fromFilter.weaponFilters));
      } else {
        filter.weaponFilters = structuredClone(fromFilter.weaponFilters);
      }
    }

    if (sections.myArmour) {
      if (sections.appendToMyEquipment) {
        filter.armourFilters = filter.armourFilters.concat(structuredClone(fromFilter.armourFilters));
      } else {
        filter.armourFilters = structuredClone(fromFilter.armourFilters);
      }
    }

    if (sections.myJewellery) {
      if (sections.appendToMyEquipment) {
        filter.jewelleryFilters = filter.jewelleryFilters.concat(structuredClone(fromFilter.jewelleryFilters));
      } else {
        filter.jewelleryFilters = structuredClone(fromFilter.jewelleryFilters);
      }
    }

    if (sections.myJewel) {
      if (sections.appendToMyEquipment) {
        filter.jewelFilters = filter.jewelFilters.concat(structuredClone(fromFilter.jewelFilters));
      } else {
        filter.jewelFilters = structuredClone(fromFilter.jewelFilters);
      }
    }

    if (sections.myFlasks) {
      if (sections.appendToMyEquipment) {
        filter.flaskFilters = filter.flaskFilters.concat(structuredClone(fromFilter.flaskFilters));
      } else {
        filter.flaskFilters = structuredClone(fromFilter.flaskFilters);
      }
    }

    if (sections.tierCosmetics) {
      filter.tierCosmetics = structuredClone(fromFilter.tierCosmetics);
      filter.divCardTierCosmetics = structuredClone(fromFilter.divCardTierCosmetics);
      filter.uniqueTierCosmetics = structuredClone(fromFilter.uniqueTierCosmetics);
      filter.chanceBaseTierCosmetics = structuredClone(fromFilter.chanceBaseTierCosmetics);
      filter.equipmentTierCosmetics = structuredClone(fromFilter.equipmentTierCosmetics);

      filter.divCardCosmeticsSameAsTierCosmetics = fromFilter.divCardCosmeticsSameAsTierCosmetics;
      filter.equipmentCosmeticsSameAsTierCosmetics = fromFilter.equipmentCosmeticsSameAsTierCosmetics;
      filter.divCardCosmeticsSameAsTierCosmeticsOverrides = structuredClone(fromFilter.divCardCosmeticsSameAsTierCosmeticsOverrides);
      filter.equipmentCosmeticsSameAsTierCosmeticsOverrides = structuredClone(fromFilter.equipmentCosmeticsSameAsTierCosmeticsOverrides);

      filter.craftingBasesStyleOverride = structuredClone(fromFilter.craftingBasesStyleOverride);
      filter.unidentifiedItemsStyleOverride = structuredClone(fromFilter.unidentifiedItemsStyleOverride);
      filter.identifiedItemsStyleOverride = structuredClone(fromFilter.identifiedItemsStyleOverride);
      filter.levelingItemsStyleOverride = structuredClone(fromFilter.levelingItemsStyleOverride);
      filter.salvagingItemsStyleOverride = structuredClone(fromFilter.salvagingItemsStyleOverride);
      filter.myItemsStyleOverride = structuredClone(fromFilter.myItemsStyleOverride);
      filter.gemStyleOverride = structuredClone(fromFilter.gemStyleOverride);
      filter.mapStyleOverride = structuredClone(fromFilter.mapStyleOverride);
      filter.goldStyleOverride = structuredClone(fromFilter.goldStyleOverride);
      filter.verisiumStyleOverride = structuredClone(fromFilter.verisiumStyleOverride);

      filter.customRuleStyles = structuredClone(fromFilter.customRuleStyles);
    }

    if (sections.customCosmeticRules) {
      if (sections.appendToCustomCosmeticRules) {
        const targetDefault = getDefaultCustomRuleGroup(filter.customCosmeticRuleGroups);
        const incoming = flattenCosmeticCustomRulesInOrder(fromFilter).map(r => structuredClone(r));
        targetDefault.rules = targetDefault.rules.concat(incoming);
        targetDefault.rules.forEach((r, index) => {
          r.order = index;
        });
      } else {
        filter.customCosmeticRuleGroups = structuredClone(fromFilter.customCosmeticRuleGroups);
      }
    }

    if (sections.customRules) {
      if (sections.appendToCustomRules) {
        const targetDefault = getDefaultCustomRuleGroup(filter.customRuleGroups);
        const incoming = flattenCustomRulesInOrder(fromFilter).map(r => structuredClone(r));
        targetDefault.rules = targetDefault.rules.concat(incoming);
        targetDefault.rules.forEach((r, index) => {
          r.order = index;
        });
      } else {
        filter.customRuleGroups = structuredClone(fromFilter.customRuleGroups);
      }
    }

    if (sections.freeRules) {
      filter.freeRulesTop = fromFilter.freeRulesTop;
      filter.freeRulesBottom = fromFilter.freeRulesBottom;
    }

    if (sections.customCurrencyAndDivCardTiers) {
      filter.tieringMode = fromFilter.tieringMode;
      filter.economy = fromFilter.economy;
      filter.customCurrencyTiers = structuredClone(fromFilter.customCurrencyTiers);
      this.currencyTierService.setTieringMode(filter.tieringMode, filter);
    }

    if (sections.tieringModeCustom) {
      filter.tieringModeCustom = structuredClone(fromFilter.tieringModeCustom);
      this.currencyTierService.setTieringMode(filter.tieringMode, filter);
    }

    if (sections.uniquesTieringModeCustom) {
      filter.uniquesTieringModeCustom = structuredClone(fromFilter.uniquesTieringModeCustom);
      this.uniqueTierService.setTieringMode(filter.uniquesTieringMode, filter);
    }

    if (sections.customUniqueTiers) {
      filter.customUniqueTiers = structuredClone(fromFilter.customUniqueTiers);
      this.uniqueTierService.combineTieredUniques(filter);
    }

    if (sections.customChanceBaseTiers) {
      filter.customChanceBaseTiers = structuredClone(fromFilter.customChanceBaseTiers);
      this.chanceBaseTierService.combineTieredChanceBases(filter);
    }

    if (sections.customGearTiers) {
      filter.customGearTiers = structuredClone(fromFilter.customGearTiers);
      this.gearTierService.combineGearTierListData(filter);
    }

    if (sections.customJewelleryTiers) {
      filter.customJewelleryTiers = structuredClone(fromFilter.customJewelleryTiers);
      this.jewelleryTierService.combineJewelleryTierListData(filter);
    }

    if (sections.customModTiers) {
      filter.customModTiers = structuredClone(fromFilter.customModTiers);
      filter.customModItemCategories = structuredClone(fromFilter.customModItemCategories);
      this.modTierService.combineModTierListData(filter);
    }

    if (sections.customEquipmentTiers) {
      filter.equipmentTieringMode = fromFilter.equipmentTieringMode;
      filter.customEquipmentTiers = structuredClone(fromFilter.customEquipmentTiers);
      this.equipmentTierService.setTieringMode(filter.equipmentTieringMode, filter);
    }

    if (sections.identifiedItemConfigs) {
      filter.identifiedItemConfigs = structuredClone(fromFilter.identifiedItemConfigs);
    }

    this.updateFilter();
    this.filterStoreService.dirtyFilterChanged.update(c => c + 1);
  }


  updateCurrencyItemTier(baseType: string, tier: Tier, options?: { shownExplicitly?: boolean }) {
    const existingItem = this.filterStoreService.activeFilter().customCurrencyTiers.find(item => item.name === baseType);
    if (existingItem) {
      existingItem.tier = tier;
      if (options?.shownExplicitly === true) {
        existingItem.shownExplicitly = true;
      } else if (options?.shownExplicitly === false) {
        delete existingItem.shownExplicitly;
      }
    } else {
      const entry: { name: string, tier: Tier, shownExplicitly?: boolean } = { name: baseType, tier };
      if (options?.shownExplicitly === true) {
        entry.shownExplicitly = true;
      }
      this.filterStoreService.activeFilter().customCurrencyTiers.push(entry);
    }

    this.currencyTierService.combineCurrencyTierListData(this.filterStoreService.activeFilter());
    this.updateFilter();
  }

  resetCurrencyItemTier(baseType: string) {
    this.filterStoreService.activeFilter().customCurrencyTiers = this.filterStoreService.activeFilter().customCurrencyTiers.filter(item => item.name !== baseType);
    this.currencyTierService.combineCurrencyTierListData(this.filterStoreService.activeFilter());
    this.updateFilter();
  }

  resetAllCurrencyItemTiers() {
    this.filterStoreService.activeFilter().customCurrencyTiers = [];
    this.currencyTierService.combineCurrencyTierListData(this.filterStoreService.activeFilter());
    this.updateFilter();
  }

  updateChanceBaseItemTier(baseType: string, tier: ChanceBaseTierType) {
    const actualTier = tier === 'Class' ? 'Other' : tier;
    const existingItem = this.filterStoreService.activeFilter().customChanceBaseTiers.find(item => item.baseType === baseType);
    if (existingItem) {
      existingItem.tier = actualTier;
    } else {
      this.filterStoreService.activeFilter().customChanceBaseTiers.push({ baseType, tier: actualTier });
    }

    this.chanceBaseTierService.combineTieredChanceBases(this.filterStoreService.activeFilter());
    this.updateFilter();
  }

  resetChanceBaseItemTier(baseType: string) {
    this.filterStoreService.activeFilter().customChanceBaseTiers = this.filterStoreService.activeFilter().customChanceBaseTiers.filter(item => item.baseType !== baseType);
    this.chanceBaseTierService.combineTieredChanceBases(this.filterStoreService.activeFilter());
    this.updateFilter();
  }

  resetAllChanceBaseItemTiers() {
    this.filterStoreService.activeFilter().customChanceBaseTiers = [];
    this.chanceBaseTierService.combineTieredChanceBases(this.filterStoreService.activeFilter());
    this.updateFilter();
  }

  updateUniqueItemTier(baseType: string, tier: UniqueTierType) {
    const actualTier = tier === 'Class' ? 'Other' : tier;
    const existingItem = this.filterStoreService.activeFilter().customUniqueTiers.find(item => item.baseType === baseType);
    if (existingItem) {
      existingItem.tier = actualTier;
    } else {
      this.filterStoreService.activeFilter().customUniqueTiers.push({ baseType, tier: actualTier });
    }

    this.uniqueTierService.combineTieredUniques(this.filterStoreService.activeFilter());
    this.updateFilter();
  }

  resetUniqueItemTier(baseType: string) {
    this.filterStoreService.activeFilter().customUniqueTiers = this.filterStoreService.activeFilter().customUniqueTiers.filter(item => item.baseType !== baseType);
    this.uniqueTierService.combineTieredUniques(this.filterStoreService.activeFilter());
    this.updateFilter();
  }

  resetAllUniqueItemTiers() {
    this.filterStoreService.activeFilter().customUniqueTiers = [];
    this.uniqueTierService.combineTieredUniques(this.filterStoreService.activeFilter());
    this.updateFilter();
  }

  updateGearItemTier(baseType: string, tier: GearTierType) {
    const existingItem = this.filterStoreService.activeFilter().customGearTiers.find(item => item.baseType === baseType);
    if (existingItem) {
      existingItem.tier = tier;
    } else {
      this.filterStoreService.activeFilter().customGearTiers.push({ baseType, tier });
    }

    this.gearTierService.combineGearTierListData(this.filterStoreService.activeFilter());
    this.updateFilter();
  }

  resetAllGearItemTiers() {
    this.filterStoreService.activeFilter().customGearTiers = [];
    this.gearTierService.combineGearTierListData(this.filterStoreService.activeFilter());
    this.updateFilter();
  }

  updateJewelleryItemTier(baseType: string, tier: JewelleryTierType) {
    const existingItem = this.filterStoreService.activeFilter().customJewelleryTiers.find(item => item.baseType === baseType);
    if (existingItem) {
      existingItem.tier = tier;
    } else {
      this.filterStoreService.activeFilter().customJewelleryTiers.push({ baseType, tier });
    }

    this.jewelleryTierService.combineJewelleryTierListData(this.filterStoreService.activeFilter());
    this.updateFilter();
  }

  resetAllJewelleryItemTiers() {
    this.filterStoreService.activeFilter().customJewelleryTiers = [];
    this.jewelleryTierService.combineJewelleryTierListData(this.filterStoreService.activeFilter());
    this.updateFilter();
  }

  updateEquipmentItemTier(equipmentType: string, tier: Tier) {
    const existingItem = this.filterStoreService.activeFilter().customEquipmentTiers.find(item => item.equipmentType === equipmentType);
    if (existingItem) {
      existingItem.tier = tier;
    } else {
      this.filterStoreService.activeFilter().customEquipmentTiers.push({ equipmentType, tier });
    }

    this.equipmentTierService.combineEquipmentTierListData(this.filterStoreService.activeFilter());
    this.updateFilter();
  }

  resetEquipmentItemTier(equipmentType: string) {
    this.filterStoreService.activeFilter().customEquipmentTiers = this.filterStoreService.activeFilter().customEquipmentTiers.filter(item => item.equipmentType !== equipmentType);
    this.equipmentTierService.combineEquipmentTierListData(this.filterStoreService.activeFilter());
    this.updateFilter();
  }

  resetAllEquipmentItemTiers() {
    this.filterStoreService.activeFilter().customEquipmentTiers = [];
    this.equipmentTierService.combineEquipmentTierListData(this.filterStoreService.activeFilter());
    this.updateFilter();
  }

  updateModTier(mod: TieredMod, tier: ModTierType) {
    const existingItem = this.filterStoreService.activeFilter().customModTiers.find(item => item.name === mod.name && item.cat === mod.cat);
    if (existingItem) {
      existingItem.tier = tier;
    } else {
      this.filterStoreService.activeFilter().customModTiers.push({
        cat: mod.cat,
        name: mod.name,
        affixType: mod.affixType,
        tier
      });
    }

    this.modTierService.combineModTierListData(this.filterStoreService.activeFilter());
    this.updateFilter();
  }

  resetModTier(mod: TieredMod) {
    this.filterStoreService.activeFilter().customModTiers = this.filterStoreService.activeFilter().customModTiers.filter(item => item.name !== mod.name || item.cat !== mod.cat);
    this.modTierService.combineModTierListData(this.filterStoreService.activeFilter());
    this.updateFilter();
  }

  resetAllModTiers() {
    const customCategories = this.filterStoreService.activeFilter().customModItemCategories;
    this.filterStoreService.activeFilter().customModTiers = this.filterStoreService.activeFilter().customModTiers.filter(mod => customCategories.some(cat => cat.name === mod.cat));
    this.modTierService.combineModTierListData(this.filterStoreService.activeFilter());
    this.updateFilter();
  }
}
